
import React, { useState } from 'react';
import {
    ShieldCheck,
    ChevronRight,
    Smartphone,
    UserCircle,
    Building2,
    Camera,
    CreditCard,
    MapPin,
    FileText,
    Briefcase,
    AlertCircle,
    CheckCircle2,
    Lock,
    Zap,
    TrendingUp,
    HelpCircle,
    Landmark,
    Scale,
    Gift,
    Headphones,
    AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/lib/utils';
import { useKYC, KYCStatus } from '@/shared/hooks/useKYC';

import { useAuth } from '@/shared/hooks/useAuth';

const VerificationPage: React.FC = () => {
    const { user, loading: authLoading } = useAuth();
    const { status, loading: kycLoading } = useKYC(user?.uid);
    const navigate = useNavigate();
    const [kycRejection, setKycRejection] = React.useState<Record<string, string>>({});

    // Listen to rejection reasons from Firestore
    React.useEffect(() => {
        if (!user?.uid) return;
        import('firebase/firestore').then(({ doc, onSnapshot }) => {
            import('@/lib/firebase').then(({ db }) => {
                const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (snap) => {
                    if (snap.exists()) {
                        setKycRejection(snap.data().kyc_rejection || {});
                    }
                });
                return unsubscribe;
            });
        });
    }, [user?.uid]);

    if (authLoading || !user || kycLoading || !status) {
        return <div className="min-h-screen flex items-center justify-center">
            <TrendingUp className="h-12 w-12 text-indigo-500 animate-bounce" />
        </div>;
    }

    const steps = [
        {
            group: '기본 내역',
            items: [
                { id: 'phone', title: '휴대폰 본인 확인', icon: Smartphone, status: status.phone, level: 1 },
                { id: 'account', title: '계좌 실명 인증', icon: Building2, status: status.account, level: 1 },
            ]
        },
        {
            group: '일반 인증 (Advanced)',
            items: [
                { id: 'idcard', title: '신분증/운전면허증', icon: UserCircle, status: status.idcard, level: 2 },
                { id: 'selfie', title: '셀카 인증', icon: Camera, status: status.selfie, level: 2 },
            ]
        },
        {
            group: '고급 인증 (Professional)',
            items: [
                { id: 'creditCard', title: '신용카드 등록', icon: CreditCard, status: status.creditCard, level: 3 },
                { id: 'address', title: '거주지 증명', icon: MapPin, status: status.address, level: 3 },
                { id: 'business', title: '기업/사업자 인증', icon: FileText, status: status.business, level: 3 },
            ]
        }
    ];

    const statusKorean: Record<string, string> = {
        'Completed': '✅ 인증 완료',
        'Pending': '⏳ 승인 대기 중',
        'Action Required': '📋 인증 필요',
        'Rejected': '❌ 인증 거절',
        'Locked': '🔒 잠김',
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Completed': return 'text-emerald-500 bg-emerald-500/10';
            case 'Pending': return 'text-amber-500 bg-amber-500/10';
            case 'Action Required': return 'text-blue-500 bg-blue-500/10';
            case 'Rejected': return 'text-red-500 bg-red-500/10';
            default: return 'text-slate-500 bg-white/5';
        }
    };

    const handleItemClick = (id: string, status: string) => {
        // if (status === 'Locked') return; // Removed lock check
        if (id === 'phone') navigate('/verification/phone');
        if (id === 'account') navigate('/verification/account');
        if (id === 'idcard') navigate('/verification/id-card');
        if (id === 'selfie') navigate('/verification/selfie');
        if (id === 'creditCard') navigate('/verification/credit-card');
        if (id === 'address') navigate('/verification/address');
        if (id === 'business') navigate('/verification/business');
    };

    const progressWidth = status.level === 0 ? '5%' : status.level === 1 ? '33%' : status.level === 2 ? '66%' : '100%';

    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-1000">
            <div className="max-w-5xl mx-auto px-4 lg:px-8 space-y-12">

                {/* Hero / Overview Section */}
                <div className="relative group rounded-[3rem] bg-gradient-to-br from-indigo-600 to-blue-700 dark:from-indigo-500 dark:to-blue-600 p-10 md:p-12 overflow-hidden shadow-2xl transition-all duration-700">
                    <div className="absolute top-0 right-0 w-80 h-80 bg-white/10 blur-3xl rounded-full -mr-20 -mt-20 group-hover:bg-white/20 transition-colors duration-1000"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/10 blur-3xl rounded-full -ml-32 -mb-32"></div>

                    <div className="relative z-10 flex flex-col md:flex-row items-center gap-12">
                        <div className="w-24 h-24 rounded-[2rem] bg-white/20 backdrop-blur-xl flex items-center justify-center border border-white/20 shadow-2xl group-hover:scale-110 transition-transform duration-700 shrink-0">
                            <ShieldCheck className="h-10 w-10 text-white fill-white/10" />
                        </div>
                        <div className="flex-1 space-y-6 text-center md:text-left">
                            <div className="space-y-2">
                                <h1 className="text-4xl md:text-5xl font-black text-white tracking-tighter uppercase leading-none">KYC 인증</h1>
                                <p className="text-white/70 font-bold text-sm md:text-base uppercase tracking-tight italic">
                                    신뢰할 수 있는 사용자 인증 및 자산 보호 프로토콜 [v3.1.3]
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center gap-8 pt-4">
                                <div className="flex-1 w-full space-y-3">
                                    <div className="flex justify-between text-[10px] font-black text-white/50 uppercase tracking-[0.2em] italic">
                                        <span>인증 진행 상태</span>
                                        <span>Level {status.level} 진행 중</span>
                                    </div>
                                    <div className="h-2.5 w-full bg-white/10 rounded-full overflow-hidden p-0.5 border border-white/5">
                                        <div
                                            className="h-full bg-white rounded-full shadow-[0_0_10px_rgba(255,255,255,0.5)] transition-all duration-1000"
                                            style={{ width: progressWidth }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="hidden sm:block w-px h-12 bg-white/20"></div>
                                <div className="text-center bg-white/10 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/10 min-w-[120px]">
                                    <p className="text-[10px] font-black text-white/50 uppercase tracking-widest leading-none mb-1">현재 인증 등급</p>
                                    <p className="text-2xl font-black text-white tabular-nums tracking-tighter">LV.{status.level}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* KYC Information Section */}
                <div className="space-y-8">
                    {/* 1. What is KYC? */}
                    <div className="bg-card border border-border rounded-3xl p-8 shadow-sm">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                                <HelpCircle className="h-6 w-6 text-emerald-500" />
                            </div>
                            <h2 className="text-xl font-black text-emerald-600 dark:text-emerald-400">KYC란 무엇인가요?</h2>
                        </div>
                        <p className="text-muted-foreground leading-relaxed font-medium">
                            KYC(Know Your Customer, 고객확인제도)는 금융 기관이 고객의 신원을 확인하는 과정입니다.
                            이는 자금 세탁, 테러 자금 조달, 신원 도용과 같은 불법 활동을 방지하기 위한 중요한 규제 요구사항입니다.
                        </p>
                    </div>

                    {/* 2. Why KYC is needed & 3. Enhanced Security */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-card border border-border rounded-3xl p-8 shadow-sm space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center">
                                    <ShieldCheck className="h-6 w-6 text-blue-500" />
                                </div>
                                <h2 className="text-xl font-black text-blue-600 dark:text-blue-400">KYC 인증이 필요한 이유</h2>
                            </div>
                            <div className="space-y-6">
                                <div className="flex gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                                        <ShieldCheck className="h-5 w-5 text-slate-500" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold mb-1">안전한 거래 환경 조성</h3>
                                        <p className="text-sm text-muted-foreground">본인 확인을 통해 불법 거래나 사기를 방지하고 안전한 거래 환경을 만듭니다.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                                        <Scale className="h-5 w-5 text-slate-500" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold mb-1">법적 규제 준수</h3>
                                        <p className="text-sm text-muted-foreground">국제적인 자금세탁방지(AML) 규정을 준수하여 합법적인 서비스를 제공합니다.</p>
                                    </div>
                                </div>
                                <div className="flex gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                                        <Landmark className="h-5 w-5 text-slate-500" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold mb-1">금융 서비스 접근성 향상</h3>
                                        <p className="text-sm text-muted-foreground">KYC 인증 완료 시 입출금 한도 상향 등 더 많은 금융 서비스를 이용할 수 있습니다.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {/* Security Card */}
                            <div className="bg-card border border-border rounded-3xl p-8 shadow-sm">
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center">
                                        <Lock className="h-6 w-6 text-indigo-500" />
                                    </div>
                                    <h2 className="text-xl font-black text-indigo-600 dark:text-indigo-400">계정 보안 강화</h2>
                                </div>
                                <p className="text-muted-foreground font-medium">
                                    본인 인증을 통해 계정 도용 위험을 줄이고 자산을 안전하게 보호합니다.
                                </p>
                            </div>

                            {/* Benefits Card */}
                            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 rounded-3xl p-8 relative overflow-hidden">
                                <div className="relative z-10">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/20">
                                            <Gift className="h-6 w-6 text-white" />
                                        </div>
                                        <h2 className="text-xl font-black text-emerald-600 dark:text-emerald-400">KYC 인증 완료 시 혜택</h2>
                                    </div>
                                    <ul className="space-y-4">
                                        {[
                                            { icon: TrendingUp, text: '일일 출금 한도 상향 (최대 100 BTC/일)' },
                                            { icon: Building2, text: '대출 및 스테이킹 서비스 이용 가능' },
                                            { icon: Gift, text: '프리미엄 이벤트 참여 자격 부여' },
                                            { icon: Headphones, text: 'VIP 고객 지원 서비스 이용' },
                                            { icon: Zap, text: '거래 수수료 할인 (최대 25%)' },
                                        ].map((item, idx) => (
                                            <li key={idx} className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-white/50 dark:bg-black/20 flex items-center justify-center shrink-0">
                                                    <item.icon className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                                </div>
                                                <span className="text-sm font-bold text-emerald-900 dark:text-emerald-100">{item.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Visual Separator for Procedure */}
                <div className="flex items-center gap-4 py-4">
                    <div className="h-px bg-border flex-1"></div>
                    <span className="text-sm font-black text-muted-foreground uppercase tracking-widest px-4 py-1 rounded-full bg-accent">KYC 인증 절차</span>
                    <div className="h-px bg-border flex-1"></div>
                </div>

                {/* Verification Tracks */}
                <div className="space-y-16">
                    {steps.map((group, idx) => (
                        <div key={idx} className="space-y-8">
                            <div className="flex items-center gap-4 px-4 overflow-hidden relative">
                                <span className="text-[11px] font-black text-muted-foreground uppercase tracking-[0.4em] italic whitespace-nowrap">{group.group}</span>
                                <div className="h-px w-full bg-gradient-to-r from-border to-transparent"></div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {group.items.map((item) => (
                                    <div
                                        key={item.id}
                                        onClick={() => handleItemClick(item.id, item.status)}
                                        className={cn(
                                            "group relative p-8 rounded-[2.5rem] bg-card border border-border transition-all duration-500 overflow-hidden shadow-xl hover:border-indigo-500/30 cursor-pointer hover:bg-accent/50 hover:shadow-2xl translate-y-0 hover:-translate-y-1"
                                        )}
                                    >
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/[0.02] blur-3xl rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>

                                        <div className="relative z-10 flex items-center justify-between gap-6">
                                            <div className="flex items-center gap-6">
                                                <div className={cn(
                                                    "w-16 h-16 rounded-[1.25rem] flex items-center justify-center transition-all duration-700 border shadow-lg group-hover:scale-110",
                                                    item.status === 'Completed'
                                                        ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20"
                                                        : "bg-accent text-muted-foreground border-border group-hover:bg-indigo-500/10 group-hover:text-indigo-600 group-hover:border-indigo-500/20"
                                                )}>
                                                    <item.icon className="h-7 w-7" />
                                                </div>
                                                <div className="space-y-2">
                                                    <h4 className="font-black text-foreground text-base group-hover:text-indigo-600 transition-colors uppercase tracking-tight">{item.title}</h4>
                                                    <div className={cn(
                                                        "inline-flex items-center px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-[0.2em] border shadow-sm",
                                                        getStatusColor(item.status)
                                                    )}>
                                                        {statusKorean[item.status] || item.status}
                                                    </div>
                                                    {/* 거절 사유 표시 */}
                                                    {item.status === 'Rejected' && kycRejection[item.id] && (
                                                        <div className="mt-2 p-3 rounded-xl bg-red-500/5 border border-red-500/20 text-[10px] text-red-400 font-bold leading-relaxed whitespace-pre-wrap">
                                                            사유: {kycRejection[item.id]?.replace(/\\n/g, '\n')}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="shrink-0 font-sans">
                                                <div className="w-12 h-12 rounded-2xl bg-accent border border-border flex items-center justify-center text-muted-foreground group-hover:bg-indigo-600 group-hover:text-white group-hover:border-indigo-500 transition-all duration-500">
                                                    {item.status === 'Completed' ? <CheckCircle2 className="h-6 w-6" /> : <ChevronRight className="h-5 w-5" />}
                                                </div>
                                            </div>
                                        </div>

                                        {item.status === 'Action Required' && (
                                            <div className="mt-6 p-4 rounded-2xl bg-indigo-500/5 border border-indigo-500/10 flex items-center gap-4 animate-pulse">
                                                <AlertCircle className="h-4 w-4 text-indigo-500" />
                                                <span className="text-[10px] font-black text-indigo-600 dark:text-indigo-500 uppercase tracking-widest italic font-sans leading-none">지금 인증을 완료하고 한도를 상향하세요</span>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Caution Section */}
                    <div className="bg-amber-500/5 border border-amber-500/20 rounded-3xl p-8 mb-12">
                        <div className="flex items-center gap-4 mb-6">
                            <AlertTriangle className="h-6 w-6 text-amber-500" />
                            <h3 className="text-lg font-black text-amber-600 dark:text-amber-500">주의사항</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-muted-foreground list-disc pl-5">
                            <li className="font-medium">모든 정보는 본인 명의의 것이어야 합니다.</li>
                            <li className="font-medium">신분증과 셀카는 선명하고 식별 가능해야 합니다.</li>
                            <li className="font-medium">제출된 정보는 암호화되어 안전하게 보관됩니다.</li>
                            <li className="font-medium text-amber-600 dark:text-amber-500">허위 정보 제출 시 서비스 이용이 제한될 수 있습니다.</li>
                        </ul>
                    </div>
                </div>

                {/* Value Proposition Footer */}
                <div className="relative p-10 rounded-[3rem] bg-accent/20 border border-border overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/[0.02] blur-3xl rounded-full"></div>
                    <div className="relative z-10 space-y-8">
                        <div className="flex items-center gap-4">
                            <div className="p-2 rounded-lg bg-emerald-500/10">
                                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-500" />
                            </div>
                            <h4 className="text-[11px] font-black text-foreground uppercase tracking-[0.3em] italic leading-none">기관 전용 혜택 프로토콜</h4>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
                            {[
                                { title: '무제한 유동성', desc: '최상위 등급의 출금 한도 부여', icon: Zap },
                                { title: '프라임 투자 상품', icon: TrendingUp, desc: '블록딜 및 고수익 스테이킹 접근' },
                                { title: '우선 지원 서비스', icon: UserCircle, desc: '1:1 컨시어지 전담 데스크 운영' },
                            ].map((feature, i) => (
                                <div key={i} className="space-y-3 group/item">
                                    <div className="flex items-center gap-3">
                                        {feature.icon && <feature.icon className="h-4 w-4 text-indigo-500 opacity-50 group-hover/item:opacity-100 transition-opacity" />}
                                        <p className="text-sm font-black text-foreground uppercase tracking-tight italic">{feature.title}</p>
                                    </div>
                                    <p className="text-[10px] font-bold text-muted-foreground leading-relaxed uppercase italic opacity-60">{feature.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Final Safety Note */}
                <div className="flex items-center justify-center gap-4 py-8 opacity-30">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <p className="text-[9px] font-black uppercase tracking-[0.4em]">군사 등급의 강력한 암호화 프로토콜 적용</p>
                </div>
            </div>
        </div>
    );
};

export default VerificationPage;
